import { z } from 'zod'
import {
  CRM_OPPORTUNITY_CONTRACT_VERSION,
  CRM_OPPORTUNITY_ERROR_CODES,
  crmOpportunityReadSideEffectsSchema,
  crmOpportunityStageSchema,
  crmOpportunitySummarySchema,
} from './crm-opportunity-read'

/**
 * Transport contracts preserve the existing opportunity handler field surface.
 * Domain validation remains in crm-shell-validators so legacy 400 messages and
 * precedence stay unchanged; passthrough retains harmless legacy fields.
 */
const optionalUnknown = z.unknown().optional()

export const crmOpportunityCreateRequestSchema = z.object({
  companyId: optionalUnknown,
  company_id: optionalUnknown,
  contactId: optionalUnknown,
  contact_id: optionalUnknown,
  leadId: optionalUnknown,
  lead_id: optionalUnknown,
  name: optionalUnknown,
  opportunityName: optionalUnknown,
  opportunity_name: optionalUnknown,
  stage: optionalUnknown,
  amountCents: optionalUnknown,
  amount_minor_units: optionalUnknown,
  currency: optionalUnknown,
  probability: optionalUnknown,
  expectedClose: optionalUnknown,
  expected_close: optionalUnknown,
  ownerUserId: optionalUnknown,
  owner_user_id: optionalUnknown,
  idempotencyKey: optionalUnknown,
  idempotency_key: optionalUnknown,
}).passthrough()

export const crmOpportunityPatchRequestSchema = z.object({
  name: optionalUnknown,
  opportunityName: optionalUnknown,
  opportunity_name: optionalUnknown,
  amountCents: optionalUnknown,
  amount_minor_units: optionalUnknown,
  currency: optionalUnknown,
  probability: optionalUnknown,
  expectedClose: optionalUnknown,
  expected_close: optionalUnknown,
  ownerUserId: optionalUnknown,
  owner_user_id: optionalUnknown,
  lostReason: optionalUnknown,
  lost_reason: optionalUnknown,
  active: optionalUnknown,
  stage: optionalUnknown,
  toStage: optionalUnknown,
  to_stage: optionalUnknown,
}).passthrough()

export const crmOpportunityStageRequestSchema = z.object({
  toStage: optionalUnknown,
  to_stage: optionalUnknown,
  stage: optionalUnknown,
  reason: optionalUnknown,
  stageReason: optionalUnknown,
  stage_reason: optionalUnknown,
}).passthrough()

export const crmOpportunityCreateResponseSchema = z.object({
  contract_version: z.literal(CRM_OPPORTUNITY_CONTRACT_VERSION),
  opportunity: crmOpportunitySummarySchema,
  replayed: z.boolean(),
  sideEffects: crmOpportunityReadSideEffectsSchema,
}).passthrough()

export const crmOpportunityPatchResponseSchema = z.object({
  contract_version: z.literal(CRM_OPPORTUNITY_CONTRACT_VERSION),
  opportunity: crmOpportunitySummarySchema,
  sideEffects: crmOpportunityReadSideEffectsSchema,
}).passthrough()

export const crmOpportunityStageTransitionResponseSchema = z.object({
  contract_version: z.literal(CRM_OPPORTUNITY_CONTRACT_VERSION),
  opportunity: crmOpportunitySummarySchema,
  fromStage: crmOpportunityStageSchema,
  sideEffects: crmOpportunityReadSideEffectsSchema,
}).passthrough()

export const CRM_OPPORTUNITY_WRITE_ERROR_CODES = CRM_OPPORTUNITY_ERROR_CODES

export const crmOpportunityWriteErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_OPPORTUNITY_WRITE_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }).passthrough(),
}).passthrough()

export type CrmOpportunityCreateRequest = z.infer<typeof crmOpportunityCreateRequestSchema>
export type CrmOpportunityPatchRequest = z.infer<typeof crmOpportunityPatchRequestSchema>
export type CrmOpportunityStageRequest = z.infer<typeof crmOpportunityStageRequestSchema>
export type CrmOpportunityCreateResponse = z.infer<typeof crmOpportunityCreateResponseSchema>
export type CrmOpportunityPatchResponse = z.infer<typeof crmOpportunityPatchResponseSchema>
export type CrmOpportunityStageTransitionResponse = z.infer<typeof crmOpportunityStageTransitionResponseSchema>
export type CrmOpportunityWriteErrorEnvelope = z.infer<typeof crmOpportunityWriteErrorEnvelopeSchema>
export { crmOpportunityStageSchema, crmOpportunitySummarySchema }
